if (!customElements.get('dual-swatch-selector')) {
  class DualSwatchSelector extends HTMLElement {
    constructor() {
      super();
      this.nativeSelect = this.querySelector('select');
      this.customSelect = this.querySelector('custom-select');
      this.shadeButtons = this.querySelectorAll('.js-change-shade');
      this.swatchRadios = this.querySelectorAll('input[type="radio"].js-option');
      this.urlShade = DualSwatchSelector.getUrlParam('shade');
      this.isSiblingSelector = this.closest('.product-options__siblings');

      if (this.isSiblingSelector) {
        this.customSelect?.addEventListener('change', this.handleSiblingChange.bind(this));
      } else {
        this.nativeSelect?.addEventListener('change', this.handleSelectChange.bind(this));
      }

      this.swatchRadios.forEach((swatchRadio) => {
        swatchRadio.addEventListener('change', this.handleRadioChange.bind(this));
      });

      this.shadeButtons.forEach((shadeButton) => {
        shadeButton.addEventListener('click', this.handleShadeChange.bind(this));
        if (this.urlShade?.toLowerCase() === shadeButton.dataset.selectedShade?.toLowerCase()) {
          shadeButton.click();
        }
      });

      // Nexthor: on a mouse, hovering a dot plays the film's configured ball spinning (served by
      // the partner app; 404 = this film has no animation, remembered per dot). Touch screens and
      // reduced-motion users keep the still.
      if (this.isSiblingSelector && window.matchMedia
          && matchMedia('(hover: hover) and (pointer: fine)').matches
          && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.querySelectorAll('a.opt-label--swatch[data-pid]').forEach((dot) => {
          dot.addEventListener('mouseenter', () => this.nxAnimEnter(dot));
          dot.addEventListener('mouseleave', () => this.nxAnimLeave(dot));
        });
      }

      // Nexthor: a product opens on its OWN colour group instead of "All". Only when the URL
      // carries no shade param at all (null) — a present-but-empty ?shade= means the visitor
      // explicitly chose "All" on a previous page, and that choice wins.
      if (this.urlShade === null && this.isSiblingSelector && this.dataset.ownShade) {
        const own = this.dataset.ownShade.toLowerCase();
        Array.from(this.shadeButtons)
          .find((btn) => (btn.dataset.selectedShade || '').toLowerCase() === own)
          ?.click();
      }
    }

    nxAnimEnter(dot) {
      const state = dot.dataset.nxAnim;
      if (state === 'none') return;
      const show = (url) => {
        if (!dot.matches(':hover')) return; // the pointer already left while the file loaded
        const swatch = dot.querySelector('.opt-swatch');
        if (!swatch || swatch.querySelector('.nx-anim')) return;
        swatch.style.position = 'relative';
        swatch.style.overflow = 'hidden';
        const img = document.createElement('img');
        img.className = 'nx-anim';
        img.alt = '';
        img.src = url; // re-set on every hover so the APNG starts from frame one
        img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit;pointer-events:none';
        swatch.appendChild(img);
      };
      if (state === 'ready') { show(dot.dataset.nxAnimUrl); return; }
      if (state === 'loading') return;
      dot.dataset.nxAnim = 'loading';
      const probe = new Image();
      const url = '/apps/installer/visualizer/anim?product=' + encodeURIComponent(dot.dataset.pid);
      probe.onload = () => { dot.dataset.nxAnim = 'ready'; dot.dataset.nxAnimUrl = url; show(url); };
      probe.onerror = () => { dot.dataset.nxAnim = 'none'; };
      probe.src = url;
    }

    nxAnimLeave(dot) {
      dot.querySelectorAll('.nx-anim').forEach((n) => n.remove());
    }

    handleSiblingChange(evt) {
      const productRelativeUrl = evt.detail.selectedValue.match(/\/products\/.*$/)[0];
      const link = this.querySelector(`a[href$="${CSS.escape(productRelativeUrl)}"]`);
      if (link) {
        link.closest('preload-links[active]')?.prepareLink(link);
        link.click();
      }
    }

    handleSelectChange(evt) {
      // Trigger a click event on the corresponding swatch value
      this.querySelector(`input[value="${CSS.escape(evt.target.value)}"]`)?.click();
    }

    handleRadioChange(evt) {
      if (evt.target.checked) {
        const customSelectOption = this.customSelect?.querySelector(`li[data-value="${CSS.escape(evt.target.value)}"]`);
        if (customSelectOption) {
          this.customSelect.selectOption(customSelectOption);
        }
      }
    }

    static updateURLWithShade(shade) {
      if (!Shopify.designMode) {
        const url = new URL(window.location);
        url.searchParams.set('shade', shade);
        window.history.replaceState({}, '', url);
      }
    }

    static getUrlParam(param) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    }

    static setParamInHref(href, param, value) {
      const url = new URL(href, window.location.origin);

      if (value !== '') {
        url.searchParams.set(param, value);
      } else {
        url.searchParams.delete(param);
      }

      return url.toString();
    }

    handleShadeChange(evt) {
      if (!this.dataset.featuredProduct) {
        DualSwatchSelector.updateURLWithShade(evt.target.dataset.selectedShade);
      }

      // Change the active button
      this.shadeButtons.forEach((shadeButton) => {
        shadeButton.classList.toggle('font-bold', evt.target === shadeButton);
      });

      // Hide the relevant shades
      this.querySelectorAll('[data-shade]').forEach((elem) => {
        elem.hidden = elem.dataset.shade !== evt.target.dataset.selectedShade && evt.target.dataset.selectedShade !== '';

        if (this.isSiblingSelector && elem.tagName === 'A' && !this.dataset.featuredProduct) {
          elem.href = DualSwatchSelector.setParamInHref(elem.href, 'shade', evt.target.dataset.selectedShade);
        } else if (this.isSiblingSelector && elem.classList.contains('custom-select__option') && !this.dataset.featuredProduct) {
          elem.dataset.value = DualSwatchSelector.setParamInHref(elem.dataset.value, 'shade', evt.target.dataset.selectedShade);
        }
      });

      // Select the first swatch from the group
      let foundVisibleSwatch = false;
      this.swatchRadios.forEach((radio) => {
        if (!foundVisibleSwatch && !radio.hasAttribute('hidden')) {
          radio.click();
          foundVisibleSwatch = true;
        }
      });
    }
  }

  customElements.define('dual-swatch-selector', DualSwatchSelector);
}
